import React from 'react';
import { type HTMLAttributes, forwardRef } from 'react';
import { cn } from '@frontend/shadcn/lib/utils';
import { ContentPreviewContentProps } from '../types';
import { useSocialMediaThumbnail } from '../../../hooks/useSocialMediaThumbnail';

// Base components
const PreviewContainer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('space-y-3', className)} {...props} />
  )
);
PreviewContainer.displayName = 'PreviewContainer';

const PreviewBox = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative aspect-square w-full overflow-hidden rounded-2xl bg-gray-100',
        className
      )}
      {...props}
    />
  )
);
PreviewBox.displayName = 'PreviewBox';

const PreviewText = forwardRef<HTMLParagraphElement, HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm text-gray-500', className)} {...props} />
  )
);
PreviewText.displayName = 'PreviewText';

// Media components
interface PreviewVideoProps extends HTMLAttributes<HTMLVideoElement> {
  src: string;
  onError?: (e: React.SyntheticEvent<HTMLVideoElement, Event>) => void;
}

const PreviewVideo = forwardRef<HTMLVideoElement, PreviewVideoProps>(
  ({ className, src, onError, ...props }, ref) => (
    <video
      ref={ref}
      src={src}
      controls
      className={cn('h-full w-full object-cover', className)}
      aria-label="Content video"
      onError={onError}
      {...props}
    >
      <track kind="captions" src="" label="English" />
    </video>
  )
);
PreviewVideo.displayName = 'PreviewVideo';

interface PreviewImageProps extends HTMLAttributes<HTMLImageElement> {
  src: string;
  alt?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
}

const PreviewImage = forwardRef<HTMLImageElement, PreviewImageProps>(
  ({ className, src, alt = 'Content preview', onError, ...props }, ref) => (
    <img
      ref={ref}
      src={src}
      alt={alt}
      className={cn('h-full w-full object-cover', className)}
      onError={onError}
      {...props}
    />
  )
);
PreviewImage.displayName = 'PreviewImage';

interface PreviewCarouselIndicatorProps extends HTMLAttributes<HTMLDivElement> {
  show?: boolean;
}

const PreviewCarouselIndicator = forwardRef<HTMLDivElement, PreviewCarouselIndicatorProps>(
  ({ className, show = true, ...props }, ref) => {
    if (!show) return null;

    return (
      <div
        ref={ref}
        className={cn('absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 text-xs text-white', className)}
        {...props}
      >
        Carousel
      </div>
    );
  }
);
PreviewCarouselIndicator.displayName = 'PreviewCarouselIndicator';

interface ContentPreviewRootProps extends ContentPreviewContentProps, HTMLAttributes<HTMLDivElement> {}

export const ContentPreviewContent = forwardRef<HTMLDivElement, ContentPreviewRootProps>(
  ({ mediaUrl, contentType, originalUrl, className, ...props }, ref) => {
    const { imageUrl, isLoading, error, contentType: detectedContentType, onImageError } = useSocialMediaThumbnail({
      postUrl: originalUrl || '',
      postImageUrl: mediaUrl,
    });

    const finalContentType = contentType || detectedContentType;
    const finalMediaUrl = imageUrl || mediaUrl;
    const isVideo = finalContentType === 'video';
    const isCarousel = finalContentType === 'carousel';

    if (isLoading) {
      return (
        <PreviewContainer>
          <PreviewBox className="animate-pulse" />
        </PreviewContainer>
      );
    }

    if (error) {
      return (
        <PreviewContainer>
          <PreviewBox className="flex items-center justify-center">
            <PreviewText>Failed to load media content</PreviewText>
          </PreviewBox>
        </PreviewContainer>
      );
    }

    return (
      <PreviewContainer ref={ref} className={className} {...props}>
        <PreviewBox>
          {isVideo ? (
            <PreviewVideo src={finalMediaUrl} onError={onImageError} />
          ) : (
            <PreviewImage src={finalMediaUrl} onError={onImageError} />
          )}
          <PreviewCarouselIndicator show={isCarousel} />
        </PreviewBox>
      </PreviewContainer>
    );
  }
);
ContentPreviewContent.displayName = 'ContentPreviewContent';

export {
  PreviewContainer,
  PreviewBox,
  PreviewText,
  PreviewVideo,
  PreviewImage,
  PreviewCarouselIndicator,
};

export default ContentPreviewContent;
